<template>
    <section class="preview">
        <div
            class="document d-flex align-items-center justify-content-center"
            v-if="!isImage && previewDocument.type != 'pdf' && previewDocument.type != 'bin'"
        >
            <font-awesome-layers full-width class="fa-10x">
                <font-awesome-icon :icon="['fas', 'file']" />
                <font-awesome-layers-text
                    class="fa-inverse"
                    transform="shrink-11.5"
                    :value="previewDocument.type ? previewDocument.type : 'doc'"
                    style="font-weight: 400"
                />
            </font-awesome-layers>
        </div>

        <slim-cropper class="document image" :options="slimOptions" v-if="isImage && previewDocument.type != 'pdf'">
            <img class="preview-image" :src="previewDocument.url" />
        </slim-cropper>

        <PreviewPDF v-if="previewDocument.type == 'pdf' || previewDocument.type == 'bin'" :url="previewDocument.url" />

        <div class="details">
            <div v-if="previewDocument.filename" class="row no-gutters d-flex align-items-center">
                <div class="col-6 text-right pr-2">
                    <el-tooltip class="item" effect="dark" content="Edit filename" placement="top">
                        <button
                            class="amb-icon-btn ml-2"
                            type="button"
                            v-if="previewDocument.uploaded"
                            @click.stop="renameFilename(previewDocument)"
                        >
                            <font-awesome-icon :icon="['fas', 'pen']" @mousedown.stop />
                        </button>
                    </el-tooltip>
                    <strong>Filename:</strong>
                </div>
                <div class="col-4 text-left">{{ previewDocument.filename }}</div>
            </div>
            <div v-if="previewDocument.url" class="row no-gutters">
                <div class="col-6 text-right pr-2"><strong>Uploaded:</strong></div>
                <div class="col-4 text-left">
                    {{ dateFormatter(previewDocument.updated_at) }}
                </div>
            </div>
            <div v-else class="row no-gutters">
                <div class="col-6 text-right pr-2"><strong>Requested:</strong></div>
                <div class="col-4 text-left">
                    {{ dateFormatter(previewDocument.created_at) }}
                </div>
            </div>
            <div v-if="previewDocument.url && previewDocument.uploader_name" class="row no-gutters">
                <div class="col-6 text-right pr-2"><strong>Uploaded By:</strong></div>
                <div class="col-4 text-left">{{ previewDocument.uploader_name }}</div>
            </div>
            <div class="row no-gutters">
                <div class="col-6 text-right pr-2"><strong>Status:</strong></div>
                <div class="col-4 text-left">
                    <el-dropdown @command="setStatus" trigger="click">
                        <span class="el-dropdown-link">
                            <el-tag size="small" :type="documentStatuses[previewDocument.status]">
                                {{ previewDocument.status }}
                            </el-tag>
                        </span>
                        <el-dropdown-menu slot="dropdown">
                            <el-dropdown-item
                                v-for="(color, status) in documentStatuses"
                                :key="status"
                                :command="status"
                                :document="previewDocument"
                            >
                                {{ status }}
                            </el-dropdown-item>
                        </el-dropdown-menu>
                    </el-dropdown>
                </div>
            </div>

            <div class="row no-gutters">
                <div class="col-6 text-right pr-2"><strong>File Size:</strong></div>
                <div class="col-4 text-left">{{ previewDocument.file_size ? previewDocument.file_size.toFixed(2) : "?" }} MB</div>
            </div>
            <div class="row no-gutters">
                <div class="col-6 text-right pr-2"><strong>Document Type:</strong></div>
                <div class="col-4 text-left">
                    <el-tooltip
                        class="item"
                        effect="dark"
                        :content="previewDocument.document_type ? previewDocument.document_type.name : ''"
                        placement="top"
                    >
                        <el-tag size="small" @click="showDocumentTypeModel = true">
                            {{ previewDocument.document_type ? previewDocument.document_type.name : "Select Document Type" }}
                        </el-tag>
                    </el-tooltip>
                    <el-dialog
                        title="Set Document Type"
                        :visible.sync="showDocumentTypeModel"
                        append-to-body
                        width="500px"
                        custom-class="set-document-type"
                    >
                        <el-select
                            v-model="previewDocument.document_type_id"
                            :placeholder="
                                previewDocument.document_type ? previewDocument.document_type.name : 'Select Document Type'
                            "
                            class="w-100"
                            filterable
                            popper-class="document-types"
                        >
                            <el-option-group v-for="group in documentTypes.keys()" :key="group" :label="group">
                                <el-option
                                    v-for="item in documentTypes.get(group)"
                                    :key="item.id"
                                    :label="item.name"
                                    :value="item.id"
                                ></el-option>
                            </el-option-group>
                        </el-select>
                        <span slot="footer" class="dialog-footer">
                            <el-button @click="showDocumentTypeModel = false">Cancel</el-button>
                            <el-button type="primary" @click="changeFileType(previewDocument)">Confirm</el-button>
                        </span>
                    </el-dialog>
                </div>
            </div>
            <div class="row no-gutters" v-if="previewDocument.type == 'pdf'">
                <div class="col-6 text-right pr-2"><strong>Page Count:</strong></div>
                <div class="col-4 text-left">{{ pageCount }}</div>
            </div>
        </div>

        <div class="actions">

            <a
                class="action"
                v-if="previewDocument.url && previewDocument.type !== 'bin'"
                :href="previewDocument.url"
                target="_blank"
            >
                <font-awesome-icon :icon="['fas', 'eye']" @mousedown.stop />
                <small>View</small>
            </a>

            <div class="action" v-if="previewDocument.url" @click="handleDownload()">
                <font-awesome-icon :icon="['fas', 'file-download']" @mousedown.stop />
                <small>Download</small>
            </div>

            <file-upload
                class="action"
                :modal="true"
                v-if="
                    canUpload &&
                    !previewDocument.is_app_document &&
                    _.get(previewDocument, 'document_type.name') != 'Signature Summary' &&
                    _.get(previewDocument, 'document_type.name') != 'Digitally Signed Document' &&
                    _.get(previewDocument, 'document_type.name') != 'Pending Signed Document'
                "
                :endpoint="`/api/application/conditioning/${$store.getters.appId}/document/${previewDocument.id}`"
                @uploadSuccess="getDocuments"
                label="Drop documents here to upload <b><em>or</em></b> <span style='text-decoration: underline;'>click to browse</span>"
                :icon="true"
                text="<small>Upload</small>"
            />

            <el-popconfirm
                v-if="canDelete"
                confirmButtonText="Trash it!"
                cancelButtonText="No, Thanks"
                icon="el-icon-info"
                iconColor="red"
                title="Are you sure to trash this?"
                @confirm="removeDocument(previewDocument)"
            >
                <div slot="reference" class="action trash">
                    <font-awesome-icon :icon="['fas', 'trash']" @mousedown.stop />
                    <small>Trash</small>
                </div>
            </el-popconfirm>

            <el-popconfirm
                v-if="
                    previewDocument.hash != undefined &&
                    previewDocument.document_type != undefined &&
                    previewDocument.document_type.name !== 'Signature Summary'
                "
                confirmButtonText="Thanks"
                cancelButtonText=""
                icon="el-icon-info"
                :title="`${previewDocument.hash}`"
            >
                <div slot="reference" class="action trash">
                    <font-awesome-icon :icon="['fas', 'lock']" @mousedown.stop />
                    <small>Hash</small>
                </div>
            </el-popconfirm>
        </div>
    </section>
</template>

<script>
import moment from "moment-timezone"
import PreviewPDF from "./PreviewPDF.vue"
export default {
    name: "DocumentPreview",
    components: {
        PreviewPDF,
    },
    props: {
        previewDocument: {
            type: Object,
        },
        documentStatuses: {
            type: Object,
        },
        documentTypes: {
            type: Map,
        },
        canUpload: {
            type: Boolean,
            required: false,
            default: true,
        },
        canDelete: {
            type: Boolean,
            required: false,
            default: true,
        },
        canPreview: {
            type: Boolean,
            required: false,
            default: true,
        },
    },
    data() {
        return {
            pageCount: 0,
            showDocumentTypeModel: false,
            previewEdit: false,
        }
    },
    computed: {
        isImage() {
            let imageFormats = ["jpeg", "jpg", "png", "gif"]
            return imageFormats.includes(this.previewDocument.type)
        },
        slimOptions() {
            return {
                instantEdit: true,
                push: true,
                post: "input, output, actions",
                service: this.service,
                buttonEditLabel: '<i class="el-icon-edit-outline"></i>',
                buttonUploadLabel: '<i class="el-icon-upload"></i>',
                buttonRemoveLabel: '<i class="el-icon-document-delete"></i>',
                download: false,
                modal: true,
                // initialImage: this.previewDocument.url,
            }
        },
    },
    watch: {},
    mounted() {
        this.$bus.$on("filemanager::pages::count", (pageCount) => {
            this.pageCount = pageCount
        })
    },
    updated() {},
    activated() {},
    beforeDestroy() {
        this.$bus.$off("filemanager::pages::count")
        this.$bus.$off("filemanager::pdf")
    },
    methods: {
        service(formdata, progress, success, failure, slim) {
            progress(500, 1000)

            slim.requestOutput((fileData, formData) => {
                let headers = {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }

                this.$tenant
                    .post(
                        `/api/application/conditioning/${$store.getters.appId}/document/${this.previewDocument.id}`,
                        formData,
                        headers
                    )
                    .then((res) => {
                        progress(1000, 1000)
                        success("edit saved")
                        this.$forceUpdate()
                    })
                    .catch((err) => {
                        console.error(err)
                    })
            })
        },
        // format date
        dateFormatter(date) {
            return this.$moment.utc(date).tz(this.$store.getters.timezone).format("ddd DD/MM/YY LT")
        },
        removeDocument(item) {
            this.$bus.$emit("filemanager::delete", item)
            this.$bus.$emit("filemanager::trash::fetch", true)
            this.$emit("closePreview", true)
        },
        setStatus(status, item) {
            item.$attrs.document.status = status
            this.$bus.$emit("filemanager::document::filetype", { doc: item.$attrs.document, status: true })
        },
        changeFileType(item) {
            this.showDocumentTypeModel = false
            this.$bus.$emit("filemanager::document::filetype", { doc: item })
        },
        renameFilename(item) {
            this.$bus.$emit("filemanager::document::rename", item)
        },
        uploadFileLine(e, doc) {
            let file = e.target.files[0]
            this.$emit("uploadLineFile", { file: file, id: doc.id })
        },
        uploadPending(item) {
            if (!item.is_app_document) {
                let el = this.$refs["upload_field_" + item.id]
                el.click()
            }
        },
        getDocuments() {
            this.$emit("getDocuments", true)
            this.$forceUpdate()
        },
        handleDownload() {
            Ambition.Request.call({
                url: `/api/application/{application}/finalise/documents/${this.previewDocument.id}/download`,
                method: "POST",
            })
                .then((res) => {
                    let link = document.createElement("a")
                    link.href = res.data
                    link.click()
                })
                .catch((err) => {})
        },
    },
}
</script>

<style lang="scss" scoped>
// @import '@assets/sass/backend/_core-variables.scss';
.preview {
    display: flex;
    height: 100%;
    align-items: center;
    flex-direction: column;
    justify-content: space-between;
    background-color: rgb(238, 241, 246);

    .document {
        flex-grow: 1;
        width: 100%;
        max-height: 60%;
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center center;
    }

    .pdf {
        width: 100%;
        height: 100%;
    }

    .details {
        display: block;
        width: 100%;
        height: 23%;

        .text-left {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    .actions {
        height: 10%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        border-top: 1px solid #e0e2e6;

        .action {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            margin: 0 1rem;
            cursor: pointer;

            &.trash {
                &:hover {
                    .fa-trash {
                        color: #f56c6c;
                    }
                }
            }

            .action {
                display: flex;
                align-items: center;
                justify-content: center;
                flex-direction: column;
                margin: 0 1rem;
                cursor: pointer;
            }
        }

        a {
            &.action {
                color: #555555 !important;
            }
        }
    }
}
</style>
<style lang="scss">
.el-tag {
    cursor: pointer !important;
}
.el-dialog__wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    .set-document-type {
        margin: 0 !important;
    }
}
.document {
    .preview-image {
        margin-bottom: 0 !important;
    }
    &.image {
        background: transparent;
        .slim-result {
            display: flex;
            align-items: center;
            justify-content: center;
            .in {
                width: auto !important;
                max-height: 100% !important;
                position: initial;
            }
            .out {
                display: none;
                width: 0px !important;
                max-height: 100% !important;
            }
        }
    }
}
</style>
